import React from "react";
import { View } from "@surge-global-engineering/react-pdf-components";
import { Style } from "@surge-global-engineering/rpdf-types";
import { MEDIUM_MARGIN, CODE_BLOCK_MARGIN, SMALL_MARGIN } from "../configs";
import { PdfTheme, PdfSlateEndnote, PdfSlateBaseParentBlockNode, PdfSlateTextNode, PdfSlateListBlock, PdfSlateImage, PdfSlateLink, PdfSlateProfileLinks } from "../types";
import Paragraph from "./Paragraph";
import List from "./List";
import SocialProfile from "./SocialProfiles";
import { SlateBlockquote } from "../../../../types/slate";
import { Blockquote } from "./Blockquote";
import Image from "./Image";
import { SlateCalloutBox } from "../../../../types/slate";
import { CalloutBox } from "./calloutbox";
import { TextMessages } from "./TextMessages";
import { TextMessagesGroupNode } from "../../../Plate/plugins/text-message/types";

interface BaseChildNodeProps {
  theme: PdfTheme;
  lightText?: boolean;
  nodeIndex: number;
  parentType?: string;
  endNotes: PdfSlateEndnote[];
  style?: Style;
  noIndent?: boolean;
  isAfterSceneBreak?: boolean;

  /**
   * showFirstSentenceFormatting - A value that can be defined using "Chapter Level" configuration
   * to toggle first sentence formatting on/off.
   *
   * @type {boolean}
   */
  showFirstSentenceFormatting: boolean;
  endNoteOffset?: number;
  sceneIndex?: number;
  previousNode?: string;
}



const BaseChildNode: React.FC<BaseChildNodeProps> = ({ theme, lightText, nodeIndex, parentType, endNotes, style, noIndent, children, isAfterSceneBreak, showFirstSentenceFormatting, endNoteOffset, sceneIndex, previousNode }) => {
    const _children = children as (PdfSlateBaseParentBlockNode | PdfSlateTextNode | PdfSlateListBlock | PdfSlateEndnote | PdfSlateImage | PdfSlateLink)[];
    const { paragraph, beginFirstSentence } = theme.properties;
    if (!children) return <></>;
    let shouldResetNodeIndex = false;  //reset nodeIndex to apply dropcaps
    
    return (
        <>
            {_children.map((child, index) => {
                const { children, type } = child as PdfSlateBaseParentBlockNode;
                const subheadingTypes = ["h2", "h3", "h4", "h5", "h6"];
                let combinedIndex = 0;

                if (previousNode === "ornamental-break" && beginFirstSentence === "break" && !type.includes("p") && isAfterSceneBreak && nodeIndex === 0) {
                  shouldResetNodeIndex = true;
                }

                if (nodeIndex === 0 && sceneIndex === 0) {
                  shouldResetNodeIndex = true;
                }

                if (!children && type !== "endnote") {
                    const _child = child as PdfSlateTextNode;
                    if(!_child.text) return null;
                    const textNode = [_child];
                    return (
                      <Paragraph
                        key={`base-child-para-${nodeIndex + index}`}
                        theme={theme}
                        nodeIndex={nodeIndex}
                        endNotesOffset={endNoteOffset}
                        endNotes={endNotes}
                        style={style}
                        parentType={parentType}
                        indented={noIndent ? false : undefined}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                      >
                        {textNode}
                      </Paragraph>
                    );
                }

                if (["align_left"].includes(type)) {
                    return (
                      <BaseChildNode
                        key={`base-child-${nodeIndex + index}`}
                        theme={theme}
                        nodeIndex={index + nodeIndex}
                        endNotes={endNotes}
                        parentType={type}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                        endNoteOffset={endNoteOffset}                    
                      >
                        {children}
                      </BaseChildNode>
                    );
                }

                if (["align_center", "align_right"].includes(type)) {
                  const textAlign = type === "align_center" ? "center" : "right";
                  return (
                    <View key={`base-child-${nodeIndex + index}`}>
                      <BaseChildNode
                        theme={theme}
                        nodeIndex={index + nodeIndex}
                        endNotes={endNotes}
                        parentType={type}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                          endNoteOffset={endNoteOffset}
                        style={{textAlign}}
                      >
                        {children}
                      </BaseChildNode>
                    </View>
                  );
                }


                if (type === "blockquote") {
                  return (
                    <Blockquote
                      index={index}
                      key={`blockquote-component-${index}`}
                      nodeIndex={index}
                      theme={theme}
                      endNotes={endNotes}
                      style={style}
                      blockquoteNode={child as SlateBlockquote}
                      parentType={parentType || ""}
                    >
                      {children}
                    </Blockquote>
                  );
                }

                if (type === "profile") {
                  const profileNode = child as PdfSlateProfileLinks;
                  return (
                    <SocialProfile key={`profile-${index}`} theme={theme} profileNode={profileNode}></SocialProfile>
                  );
                }

                if (type === "code_block") {
                    const canJustifyText = !["align_center", "align_right"].includes(parentType as string);
                    return (
                      <Paragraph
                        key={`code-block-${index}`}
                        theme={theme}
                        nodeIndex={index + 10}
                        endNotes={endNotes}
                        endNotesOffset={endNoteOffset}
                        indented={false}
                        parentType={type}
                        canJustifyText={canJustifyText}
                        style={{
                          ...style,
                          marginTop: MEDIUM_MARGIN,
                          marginBottom: MEDIUM_MARGIN,
                          marginLeft: CODE_BLOCK_MARGIN,
                          marginRight: CODE_BLOCK_MARGIN,
                        }}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                      >
                        {children}
                      </Paragraph>
                    );
                }

                if (type === "indent") {
                  return (
                    <Paragraph
                      key={`indent-${index}`}
                      theme={theme}
                      endNotesOffset={endNoteOffset}
                      nodeIndex={nodeIndex + 10}
                      endNotes={endNotes}
                      indented={false}
                      isHangingIndent={true}
                      parentType={type}
                      style={{
                        paddingLeft: `${theme.properties.hangingIndent}cm`,
                      }}
                      canJustifyText={true}
                      showFirstSentenceFormatting={false}
                    >
                      {children}
                    </Paragraph>
                  );
                }

                if (type === "scene") {   
                  return (
                    <BaseChildNode
                      key={`scene-${index}`}
                      theme={theme}
                      nodeIndex={nodeIndex}
                      endNotes={endNotes}
                      parentType={type}
                      showFirstSentenceFormatting={showFirstSentenceFormatting}
                    >
                      {children}
                    </BaseChildNode>
                  );
                }

                if (type === "calloutbox") {
                  const {background, border} = child as SlateCalloutBox;
                  return (
                    <CalloutBox
                      key={`calloutbox-${index}`}
                      theme={theme}
                      nodeIndex={nodeIndex}
                      endNotes={endNotes}
                      parentType={type}
                      showFirstSentenceFormatting={false}
                      index={index}
                      background={background}
                      border={border}
                   >
                      {children}
                    </CalloutBox>  
                  );
                }

                if(type === "text_messages"){
                  return (
                    <TextMessages
                        index={index}
                        key={`text-messages-${index}`}
                        nodeIndex={nodeIndex}
                        theme={theme}
                        endNotesOffset={endNoteOffset}
                        endNotes={endNotes}
                        parentType={type}
                        style = {(child as TextMessagesGroupNode).style}
                      >
                        {children}
                      </TextMessages>
                  );
                }


                if (type === "h2" || type === "h3" || type === "h4" || type === "h5" || type === "h6") {
                    const marginTop = paragraph.indent ? MEDIUM_MARGIN : SMALL_MARGIN;
                    const key = `para-${type}-${index}`;
                    const fontWeight = type === "h2" ? 900 : 600;
                    return (
                      <Paragraph
                        key={key}
                        theme={theme}
                        nodeIndex={index + 10}
                        endNotesOffset={endNoteOffset}
                        endNotes={endNotes}
                        parentType={type}
                        style={{
                          ...style,
                          marginTop,
                          marginBottom: SMALL_MARGIN,
                          fontWeight: fontWeight,
                        }}
                        canJustifyText={false}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                      >
                        {children}
                      </Paragraph>
                    );
                }

                if (["ul", "ol"].includes(type)) {
                    return (
                      <List
                        endnotes={endNotes}
                        key={`li-${index}`}
                        theme={theme}
                        type={type as "ol" | "ul"}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                        endNoteOffset={endNoteOffset}
                      >
                        {children}
                      </List>
                    );
                }

                if (type === "image") {
                  const imageNode = child as PdfSlateImage;
                  if(imageNode.separatePage) {
                      return (
                          <Image
                              key={`image-${index}`}
                              theme={theme}
                              lightTextCaption={lightText}
                              imageNode={imageNode}
                              priorNode={_children[index-2]}
                              previousNode={_children[index-1]}
                              nextNode={_children[index+1]}
                              thenNode={_children[index+2]}
                          />
                      );
                  } else {
                      return (
                          <Image key={`image-${index}`} theme={theme} imageNode={imageNode} />
                      );
                  }
              }

                if (previousNode === "ornamental-break") {
                  if (beginFirstSentence !== "break") {
                      nodeIndex++;
                  }
                }

                if (type === "page-break"){
                  const lastOnPage = (_children[index+1] === undefined && _children[index+2] === undefined);
                  if (!lastOnPage) {
                      return <View key={`page-break-${index}`} break/>;
                  }
              }

                let _index = index;
                if (parentType && ["align_center", "align_right"].includes(parentType)) {
                    _index += 10;
                }
                if (type === "endnote") {
                    return (
                      <Paragraph
                        key={`para-base-${index}`}
                        theme={theme}
                        endNotesOffset={endNoteOffset}
                        nodeIndex={_index + nodeIndex}
                        endNotes={endNotes}
                        style={style}
                        parentType={parentType}
                        indented={noIndent ? false : undefined}
                        showFirstSentenceFormatting={showFirstSentenceFormatting}
                      >
                        {[child]}
                      </Paragraph>
                    );
                }

                

                if (type === "p" && shouldResetNodeIndex) {
                  combinedIndex = nodeIndex = 0;
                  shouldResetNodeIndex = false;
                }else {
                  combinedIndex = _index + nodeIndex
                  shouldResetNodeIndex = false;
                }

                let disableIndent = false;

                const prevNode = index > 0 ? (_children[index - 1] as PdfSlateBaseParentBlockNode) : null;

                if (
                  (prevNode && prevNode.type === "ornamental-break" && theme.properties.sceneBreakShowSetting !== "no-break") ||
                  (prevNode && subheadingTypes.includes(prevNode.type)) ||
                  (isAfterSceneBreak && index === 0)
                ) {
                  disableIndent = true;
                }

                nodeIndex++;

                return (
                  <Paragraph
                    key={`para-base-${index}`}
                    theme={theme}
                    nodeIndex={combinedIndex}
                    endNotesOffset={endNoteOffset}
                    endNotes={endNotes}
                    style={style}
                    parentType={parentType}
                    disableIndent={disableIndent}
                    indented={noIndent ? false : undefined}
                    showFirstSentenceFormatting={showFirstSentenceFormatting}
                  >
                    {children}
                  </Paragraph>
                );
            })}
        </>
    );
};

export default BaseChildNode;
